import React, { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Box,
  Chip,
  Stack,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material'
import { Search as SearchIcon, Add as AddIcon } from '@mui/icons-material'
import { useSearchDatabaseQuery } from '@store/api/databaseApi'
import { useAddToWishlistMutation } from '@store/api/cigarDetailApi'

type Severity = 'success' | 'error'

interface WishlistAddDialogProps {
  open: boolean
  onClose: () => void
  onResult: (message: string, severity: Severity) => void
}

const WishlistAddDialog: React.FC<WishlistAddDialogProps> = ({ open, onClose, onResult }) => {
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [addingId, setAddingId] = useState<string | null>(null)

  const [addToWishlist] = useAddToWishlistMutation()

  // Debounce the search input (~300ms) to avoid a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  // Reset local state whenever the dialog is opened.
  useEffect(() => {
    if (open) {
      setSearchInput('')
      setDebouncedSearch('')
      setAddingId(null)
    }
  }, [open])

  const canSearch = debouncedSearch.length >= 2

  const { data, isFetching, isError, refetch } = useSearchDatabaseQuery(
    { search: debouncedSearch, limit: 8 },
    { skip: !open || !canSearch }
  )

  // Only show results that match the current debounced query; hide stale rows while refetching.
  const results = useMemo(() => {
    if (!canSearch || isFetching || isError) return []
    return data?.results ?? []
  }, [canSearch, isFetching, isError, data])

  const handleAdd = async (id: string, name: string) => {
    setAddingId(id)
    try {
      await addToWishlist({ cigarId: id }).unwrap()
      onResult(`${name} added to wishlist`, 'success')
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status
      if (status === 409) {
        onResult(`${name} is already on your wishlist`, 'success')
      } else {
        onResult('Failed to add to wishlist', 'error')
      }
    } finally {
      setAddingId(null)
    }
  }

  let resultsBody: React.ReactNode
  if (!canSearch) {
    resultsBody = (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        Type at least 2 characters to search the cigar database.
      </Typography>
    )
  } else if (isError) {
    resultsBody = (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        Failed to search cigars. Please try again.
      </Alert>
    )
  } else if (isFetching) {
    resultsBody = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    )
  } else if (results.length === 0) {
    resultsBody = (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        No cigars found for &ldquo;{debouncedSearch}&rdquo;.
      </Typography>
    )
  } else {
    resultsBody = (
      <List disablePadding>
        {results.map((result) => (
          <ListItem
            key={result.id}
            divider
            secondaryAction={
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={addingId === result.id || isFetching}
                onClick={() => handleAdd(result.id, result.name)}
              >
                Add
              </Button>
            }
          >
            <ListItemText
              primary={result.name}
              secondary={
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  <Chip label={result.brand.name} size="small" variant="outlined" />
                  {result.strengthLevel && (
                    <Chip label={result.strengthLevel} size="small" variant="outlined" />
                  )}
                  {result.vitola && (
                    <Chip label={result.vitola.name} size="small" variant="outlined" />
                  )}
                </Stack>
              }
              secondaryTypographyProps={{ component: 'div' }}
            />
          </ListItem>
        ))}
      </List>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add a Cigar to Your Wishlist</DialogTitle>
      <DialogContent dividers>
        <TextField
          autoFocus
          fullWidth
          placeholder="Search by cigar, brand, or line..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: isFetching ? (
              <InputAdornment position="end">
                <CircularProgress size={18} />
              </InputAdornment>
            ) : null,
          }}
        />

        <Box sx={{ mt: 2, minHeight: 200 }}>{resultsBody}</Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  )
}

export default WishlistAddDialog
